import FormDataAddressInterface from '@/apps/order/interfaces/ordersCreate/FormDataAddressInterface';
import FormDataCalculatorInterface from '@/apps/order/interfaces/ordersCreate/FormDataCalculatorInterface';
import FormDataCustomerTypeInterface from '@/apps/order/interfaces/ordersCreate/FormDataCustomerTypeInterface';
import FormDataCartItemInterface from '@/apps/order/interfaces/ordersCreate/FormDataCartItemInterface';
import FormDataDeclarationOfIntentInterface from '@/apps/order/interfaces/ordersCreate/FormDataDeclarationOfIntentInterface';
import FormDataPaymentInterface from '@/apps/order/interfaces/ordersCreate/FormDataPaymentInterface';
import CategoryInterface from '@/apps/order/interfaces/CategoryInterface';
import FormDataReferralMarketingInterface
  from '@/apps/order/interfaces/ordersCreate/FormDataReferralMarketingInterface';
import OrdersCreateFormDataInterface from '@/apps/order/interfaces/OrdersCreateFormDataInterface';
import ReferralRewardInterface from '@/apps/order/interfaces/ReferralRewardInterface';
import { hasMarginalPrice } from '@/apps/order/helper/marginalPrice';
import PreviousAddressInterface from '../interfaces/ordersCreate/OrderAddressInterface';
import { CustomFieldValueInterface, CustomFieldValueOptionInterface } from '@/apps/order/interfaces/CustomFieldValuesInterface';

type PhoneNumberValue = {
  type: string
  number?: string
}

class EditMapping implements OrdersCreateFormDataInterface {
  rawData: Record<string, any>;

  productId: number;

  uuid: number;

  customer: FormDataCustomerTypeInterface;

  categories: string[];

  calculator: FormDataCalculatorInterface;

  address: FormDataAddressInterface;

  cartItems: Record<string, FormDataCartItemInterface>;

  payment: FormDataPaymentInterface;

  declarationOfIntent: FormDataDeclarationOfIntentInterface;

  selectedTariffs: Record<string, any>;

  customFields: Record<string, CustomFieldValueInterface>;

  referralMarketing: FormDataReferralMarketingInterface;

  requestId: string;

  canEditAddress: boolean;

  previousAddress: PreviousAddressInterface;

  constructor(
    order: Record<string, any>,
    products: Record<string, any>[],
    categories: CategoryInterface[],
  ) {
    const category = EditMapping.getCategory(order, categories);

    this.rawData = order;

    this.productId = order.product_data.id;

    this.uuid = order.uuid;

    this.customer = {
      exists: false,
      customerNumber: order.customer_data?.customerNumber || '',
      meterNumber: '',
    };

    this.categories = products
      .map((product) => product?.category?.key)
      .filter((categoryKey) => !!categoryKey);

    // Handle cases where order_data might be missing (e.g., tariff change flow)
    const orderData = order.order_data || {};
    const usage = parseInt(orderData.usage, 10);
    const usageHt = parseInt(orderData.usageHt, 10);
    const usageNt = parseInt(orderData.usageNt, 10);

    const isDouble = Number.isNaN(usage) && (
      !Number.isNaN(usageHt) || !Number.isNaN(usageNt)
    );

    const typeOfMeasurement = EditMapping.getTypeOfMeasurement(order);

    this.calculator = {
      brand_id: order.brand_id,
      sales_channel_id: order.sales_channel_id,
      ...(order.lead_partner_id !== undefined ? { salesPartnerNumber: order.lead_partner_id } : {}),
      usage: {
        [category]: orderData.usage,
      },
      usageNt: {
        [category]: orderData.usageNt,
      },
      usageHt: {
        [category]: orderData.usageHt,
      },
      measurementType: {
        [category]: typeOfMeasurement,
      },
      subcategories: {},
      counterType: {
        [category]: isDouble ? 'double' : 'single',
      },
      customer_type: order.customer_data?.type,
      postcode: order.shipping_data?.postcode,
      city: order.shipping_data?.city,
      street: order.shipping_data?.street,
      number: order.shipping_data?.number,
      supplier: {},
      supplier_tariff: {},
      current_working_price: {},
      current_base_price: {},
    };

    const billingAddressDifferent = ['postcode', 'city', 'street', 'number', 'numberSuffix'].some((key) => order.billing_data?.[key] !== order.shipping_data?.[key]);

    this.address = {
      salutation: order.customer_data?.salutation,
      firstName: order.customer_data?.firstName,
      lastName: order.customer_data?.lastName,
      namePersonOne: order.customer_data?.name1,
      namePersonTwo: order.customer_data?.name2,
      title: order.customer_data?.title,
      sharedLastName: order.customer_data?.sharedLastName,
      coAddon: order.customer_data?.coAddon,
      birthday: order.customer_data?.birthday,
      companyName: order.customer_data?.company,
      legalForm: order.customer_data?.legalForm,
      companyContact: order.customer_data?.contactPerson,
      companyRegistrationCourt: order.customer_data?.registerCourt,
      companyRegistrationNumber: order.customer_data?.registerNumber,
      companyTaxId: order.customer_data?.taxNumber,
      email: order.customer_data?.email,
      phone: EditMapping.getPhones(order),
      houseNumberAddon: order.shipping_data?.numberSuffix,
      billingType: order.billing_data?.type,
      // Hydrate billing-address takeover so edit shows the customer's choice instead of dropping it on save.
      newDeliveryAddress: order.shipping_data?.newDeliveryAddress,
      billingAddressDifferent,
      billingAddress: billingAddressDifferent ? {
        postcode: order.billing_data?.postcode,
        city: order.billing_data?.city,
        street: order.billing_data?.street,
        houseNumber: order.billing_data?.number,
        houseNumberAddon: order.billing_data?.numberSuffix,
        pobox: order.billing_data?.pobox,
      } : {},
    };

    // Fiber Anschlussdaten + Rufnummermitnahme: flat DTOs (connection_data/number_transfer_data) → nested edit-form shape (residenceAddress{…}/connectionOwner{…}).
    const connection = order.connection_data;
    const connectionData = connection ? {
      hasPropertyConnection: connection.hasPropertyConnection,
      isPropertyOwner: connection.isPropertyOwner,
      isResidence: connection.isResidence,
      residenceAddress: {
        postcode: connection.residenceAddressPostcode,
        city: connection.residenceAddressCity,
        street: connection.residenceAddressStreet,
        houseNumber: connection.residenceAddressHouseNumber,
        houseNumberAddon: connection.residenceAddressHouseNumberAddon,
      },
      isApartmentBuilding: connection.isApartmentBuilding,
      additionalPropertyConnection: connection.additionalPropertyConnection,
    } : undefined;

    const numberTransfer = order.number_transfer_data;
    const numberTransferData = numberTransfer ? {
      isNumberTransfer: numberTransfer.isNumberTransfer,
      currentProvider: numberTransfer.currentProvider,
      phoneNumber: numberTransfer.phoneNumber,
      allowCancellation: numberTransfer.allowCancellation,
      notIsConnectionOwner: numberTransfer.notIsConnectionOwner,
      connectionOwner: numberTransfer.notIsConnectionOwner ? {
        firstName: numberTransfer.connectionOwnerFirstName,
        lastName: numberTransfer.connectionOwnerLastName,
        postcode: numberTransfer.connectionOwnerPostcode,
        city: numberTransfer.connectionOwnerCity,
        street: numberTransfer.connectionOwnerStreet,
        houseNumber: numberTransfer.connectionOwnerHouseNumber,
        houseNumberAddon: numberTransfer.connectionOwnerHouseNumberAddon,
      } : undefined,
    } : undefined;

    this.cartItems = {
      [category]: {
        consumptionPoint: {
          orderType: order.order_type,
          previousSupplierCanceled: orderData.hasSelfCancelled,
          previousSupplierCanceledAt: orderData.selfCancellationDate,
          nextPossibleChangeDate: orderData.isNextPossibleChangeDate,
          movingAt: orderData.desiredDeliveryDate,
          moveOutDate: orderData.moveOutDate,
          readingAt: orderData.dayOfReadingActualUsage,
          desiredDate: orderData.desiredDeliveryDate,
          customerNumber: orderData.customerNumber,
          previousSupplier: orderData.supplier ? JSON.stringify({
            value: Number(orderData.supplier),
            code: orderData.supplierCode,
            label: orderData.supplierName,
          }) : undefined,
          consumptionPointId: orderData.consumptionPointId,
          meterNumber: orderData.meterNumber,
          meterReading: orderData.actualMeterUsage || orderData.meterReading,
        },
        declarationOfIntent: {
          generalTerms: order.privacy_data?.isGeneralTermsAccept,
          customerContract: order.privacy_data?.isCustomerContractAccept,
          customerDisclaimer: order.privacy_data?.isCustomerDisclaimerAccept,
        },
        paymentData: {
          coupon: order.payment_data?.voucherCodes,
        },
        // Fiber selected options rebuilt from persisted order (BE wire: order.optionsData [{option, revision, selected[]}]); undefined for commodity.
        options: order.optionsData?.length ? order.optionsData : undefined,
        connectionData,
        numberTransferData,
      },
    };

    this.payment = {
      sepa: order.payment_data?.isAcceptAgreements,
      yearly: order.payment_data?.yearlyPayment,
      paymentType: order.payment_data?.type,
      accountOwner: order.payment_data?.accountHolder,
      iban: order.payment_data?.iban,
      bic: order.payment_data?.bic,
    };

    this.declarationOfIntent = {
      privacyPolicy: order.privacy_data?.isPrivacyPolicyAccept,
      collectionOfInformation: order.privacy_data?.isCollectionOfInformationAccept,
      marketingConsent: order.privacy_data?.isMarketingConsentCheck,
    };

    const validProduct = products.find(
      (product: Record<string, any>) => product.id === order.product_data.id,
    );
    const updatedKeys = ['prices_data', 'marginal_price_data'];
    // An empty Grenzpreis object counts as none; sending it back fails io.order's validation.
    const orderValue = (key: string) => (
      key === 'marginal_price_data' && !hasMarginalPrice(order[key]) ? null : order[key]
    );
    // Product keys omit order-only Grenzpreis; concat, not Set spread (ES5 spread is [] and wipes the tariff).
    const productKeys = Object.keys(validProduct || {});
    const mergedKeys = productKeys.concat(
      updatedKeys.filter((key) => orderValue(key) && !productKeys.includes(key)),
    );

    this.selectedTariffs = validProduct ? {
      // merge valid product with existing order data
      [category]: mergedKeys.reduce((acc: Record<string, any>, key: string) => {
        if (updatedKeys.includes(key)) {
          acc[key] = orderValue(key) || validProduct[key];

          return acc;
        }

        acc[key] = validProduct[key];

        return acc;
      }, {}),
    } : {};

    const customFieldFlatMap = (order.customFieldData?.fields || [])
      .reduce((acc: Record<string, any>, value: CustomFieldValueInterface) => {
        acc[value.key] = value.value;
        return acc;
      }, {});

    const productId = order?.product_data?.id;
    this.customFields = {
      [`${productId}`]: { ...customFieldFlatMap },
    };

    this.referralMarketing = {
      referred: false,
    };

    this.requestId = '';

    this.canEditAddress = false;

    this.previousAddress = order.previous_address_data ?? {};
  }

  private static getTypeOfMeasurement(order: Record<string, any>): string | undefined {
    let typeOfMeasurement: string | undefined;
    if (order.product_data.contractDetails?.doubleTariffWithHouseholdPower === true) {
      typeOfMeasurement = 'doubleTariffWithHouseholdPower';
    } else if (order.product_data.contractDetails?.doubleTariffWithoutHouseholdPower === true) {
      typeOfMeasurement = 'doubleTariffWithoutHouseholdPower';
    }

    return typeOfMeasurement;
  }

  static getPhones(order: Record<string, any>): PhoneNumberValue[] {
    return Object.entries(order.customer_data?.phoneNumbers || {}).map(([key, value]) => ({
      type: key,
      number: value,
    } as PhoneNumberValue));
  }

  static getCategory(order: Record<string, any>, categories: CategoryInterface[]): string {
    return (categories || [])
      .find((item: CategoryInterface) => item.id === order.product_data.categoryId)
      ?.key || 'power';
  }

  static getCalculationParams(
    order: Record<string, any>,
    categories: CategoryInterface[],
  ): Record<string, any> {
    const category = EditMapping.getCategory(order, categories);
    const canHaveDoubleCounterType = ['power', 'heatingPower'].includes(category);

    const orderData = order.order_data || {};
    const usage = parseInt(orderData.usage, 10);
    const usageHt = parseInt(orderData.usageHt, 10);
    const usageNt = parseInt(orderData.usageNt, 10);

    const isDouble = canHaveDoubleCounterType && Number.isNaN(usage) && (
      !Number.isNaN(usageHt) || !Number.isNaN(usageNt)
    );

    const typeOfMeasurement = EditMapping.getTypeOfMeasurement(order);

    return {
      postcode: order.shipping_data?.postcode,
      city: order.shipping_data?.city,
      street: order.shipping_data?.street,
      number: order.shipping_data?.number,
      type: EditMapping.getCategory(order, categories),
      usage: isDouble ? undefined : orderData.usage,
      usageHt: !isDouble ? undefined : orderData.usageHt,
      usageNt: !isDouble ? undefined : orderData.usageNt,
      brand_id: order.brand_id,
      customer_type: order.customer_data?.type,
      sales_channel_id: order.sales_channel_id,
      counterType: isDouble ? 'double' : 'single',
      product_id: order.product_data?.id,
      bonusCode: order.payment_data?.voucherCodes,
      typeOfMeasurement,
    };
  }

  public setReferralMarketing(referralMarketingRewards?: ReferralRewardInterface[]): void {
    if (this.rawData.referral_data?.referred) {
      this.referralMarketing = {
        ...this.rawData.referral_data,
        referrerReward: referralMarketingRewards?.find((reward) => `${reward.value}`.trim() === `${this.rawData.referral_data.referrerReward || ''}`.trim())?.value,
        customerReward: referralMarketingRewards?.find((reward) => `${reward.value}`.trim() === `${this.rawData.referral_data.customerReward || ''}`.trim())?.value,
      };
    }
  }
}

export default EditMapping;
